import axios from 'axios';
import { TOKEN, settings } from 'utils';

/**
 * 200 Response Data Object:
 * {
 *   token: string;
 * }
 */
export const getPublicToken = async () => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/access/token`,
    method: 'GET',
  })
    .then((response) => {
      return 'Bearer ' + response.data.token;
    })
    .catch(() => {
      return '';
    });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const updatePlaidFail = async (uid: string, flag: boolean) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/business/applications/call/plaid_fail`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getPublicToken(),
    },
    data: JSON.stringify({
      vars: {
        uid: uid,
        plaid_failed: flag,
      },
    }),
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const sendLeadNotification = async (id: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/notify_customer?id=${id}`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const sendConsumerCreditConsent = async () => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v2/business/consents`,
    method: 'POST',
    data: JSON.stringify({
      consent_name: 'consumer_credit_report',
      decision: 'yes',
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * Array<{
 *   id: number;
 *   institution_name: string;
 *   institution_id: string;
 *   status: string;
 *   accounts: Array<{
 *     id: number;
 *     name: string;
 *     mask: string;
 *     type: string;
 *     subtype: string;
 *     product: string;
 *     ach_verification_status?: string;
 *     verification_method?: string;
 *     is_default_ach_account?: boolean;
 *     bank_detail_id?: number;
 *     consent_given?: boolean;
 *     aba_no?: string;
 *   }>;
 * }>
 */
export const fetchPlaidItems = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/items`,
    params: {
      business_id: businessId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * Array<{
 *   id: number;
 *   institution_name: string;
 *   institution_id: string;
 *   status: string;
 *   accounts: Array<{
 *     id: number;
 *     name: string;
 *     mask: string;
 *     type: string;
 *     subtype: string;
 *     product: string;
 *     ach_verification_status?: string;
 *     verification_method?: string;
 *     is_default_ach_account?: boolean;
 *     bank_detail_id?: number;
 *     consent_given?: boolean;
 *     aba_no?: string;
 *   }>;
 * }>
 */
export const fetchPlaidAddToken = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/token`,
    params: {
      business_id: businessId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   token: string;
 * }
 */
export const fetchPlaidACHToken = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/transfer_link_token`,
    params: {
      business_id: businessId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   token: string;
 * }
 */
export const fetchPlaidUpdateToken = async (
  businessId: string,
  plaidItemId: string
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/token`,
    params: {
      business_id: businessId,
      plaid_item_id: plaidItemId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   token: string;
 * }
 */
export const fetchPlaidACHUpdateToken = async (
  businessId: string,
  plaidItemId: string | number
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/reconnect_link_token`,
    params: {
      business_id: businessId,
      plaid_item_id: plaidItemId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const submitPlaidPublicToken = async (
  businessId: string,
  publicToken: string,
  metadata: unknown,
  plaidItemId: string,
  product: string
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/submit_public_token`,
    method: 'POST',
    data: JSON.stringify({
      business_id: businessId,
      public_token: publicToken,
      metadata,
      plaid_item_id: plaidItemId,
      product,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   ach_mode?: string;
 *   config_exists?: boolean;
 * }
 */
export const fetchACHType = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/ach_status`,
    params: {
      business_id: businessId,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const achAccountConsent = async (
  businessId: string,
  bankDetailId: string,
  ipAddress: string
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/accept_ach_consent`,
    method: 'POST',
    data: JSON.stringify({
      business_id: businessId,
      bank_detail_id: bankDetailId,
      consent: {
        signed_ip: ipAddress || '0.0.0.0',
      },
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const enableACH = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/enable_ach`,
    method: 'POST',
    data: JSON.stringify({
      business_id: businessId,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   message?: string;
 *   success?: boolean;
 * }
 */
export const requestACHCallback = async (
  accountId: string,
  message: string
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/plaid/request_ach_callback`,
    method: 'POST',
    data: JSON.stringify({
      business_id: accountId,
      message,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * Array<{
 *   account_id: number;
 *   institution_name: string;
 *   bank_account_number: string;
 *   account_owner: string;
 *   bank_account_type: string | null;
 *   is_primary_account: boolean;
 *   sanitised_account_number: string;
 * }>
 */
export const getMoneyThumbBank = async (
  importerId: string,
  bankId?: string | number
) => {
  return await axios({
    url: `${
      process.env.REACT_APP_REST_API_URL
    }/v2/business/importers/${importerId}/moneythumb_bank${
      bankId ? `/${bankId}` : ''
    }`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * Array<{
 *   account_id: number;
 *   institution_name: string;
 *   bank_account_number: string;
 *   account_owner: string;
 *   bank_account_type: string | null;
 *   is_primary_account: boolean;
 *   sanitised_account_number: string;
 * }>
 */
export const getAllMoneyThumbBank = async (importerId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v2/business/importers/${importerId}/all_moneythumb_banks`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   facility_details?: {
 *     facility_size?: number;
 *     advance_rate?: string;
 *     margin_rate?: string;
 *     setup_fee?: string;
 *     max_credit_period?: number;
 *     interest_fee?: string;
 *     transaction_fee?: string;
 *     overdue_rate?: string;
 *     truepic_inspection_type?: string;
 *   };
 *   esign_details?: {
 *     esign_url?: string;
 *     esign_status?: string;
 *     onfido_completed?: boolean;
 *     esigners?: Array<{
 *       name?: string;
 *       phone?: string;
 *       email?: string;
 *       status?: string;
 *       onfido_completed?: boolean;
 *     }>;
 *   };
 * }
 */
export const fetchFacilityDetails = async (importerId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v2/business/importers/${importerId}/facility_details`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 *   data?: {
 *     id: number;
 *     status: string;
 *     name: string;
 *     address: string;
 *     contact: string;
 *     send_method: string;
 *     created_at: string;
 *     updated_at: string;
 *   }
 * }
 */
export const saveTruepicDetails = async (
  businessId: string,
  siteDetails: any
) => {
  const [firstName, ...rest] = (siteDetails.pocName || '').split(' ');
  const lastName = rest.join(' ');

  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/truepic/save_details`,
    method: 'POST',
    data: JSON.stringify({
      customer_first_name: firstName,
      customer_last_name: lastName,
      customer_email_address: siteDetails.pocEmail || '',
      customer_phone_number: siteDetails.pocPhone || '',
      customer_address: siteDetails.address || '',
      site_type: siteDetails.site_type || '',
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 *   error?: string;
 *   data?: {
 *     id: number;
 *     status: string;
 *     name: string;
 *     address: string;
 *     contact: string;
 *     send_method: string;
 *     created_at: string;
 *     updated_at: string;
 *   }
 * }
 */
export const updateTruepicDetails = async (
  businessId: string,
  inspectionId: string | number,
  siteDetails: any
) => {
  const [firstName, ...rest] = (siteDetails.pocName || '').split(' ');
  const lastName = rest.join(' ');

  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/truepic/${inspectionId}`,
    method: 'PUT',
    data: JSON.stringify({
      customer_first_name: siteDetails.pocFirstName || firstName || '',
      customer_last_name: siteDetails.pocLastName || lastName || '',
      customer_email_address: siteDetails.pocEmail || '',
      customer_phone_number: siteDetails.pocPhone || '',
      customer_address: siteDetails.address || '',
      site_type: siteDetails.site_type || '',
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 * }
 */
export const deleteTruepicDetails = async (
  businessId: string,
  inspectionId: string | number
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/truepic/${inspectionId}`,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 *   data?: {
 *     id: number;
 *     status: string;
 *     name: string;
 *     address: string;
 *     contact: string;
 *     send_method: string;
 *   }
 * }
 */
export const sendTruepicLinks = async (
  businessId: string,
  siteIds: string[]
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/truepic/send_links`,
    method: 'POST',
    data: JSON.stringify({
      inspection_ids: siteIds,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 *   data?: {
 *     id: number;
 *     status: string;
 *     name: string;
 *     address: string;
 *     contact: string;
 *     send_method: string;
 *   }
 * }
 */
export const getTruepicDetails = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/truepic/status`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 *   data?: {
 *     status: string;
 *     onboarding_stage: string;
 *   }
 * }
 */
export const submitTruepicApplication = async (businessId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/submit_application`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   success?: boolean;
 * }
 */
export const startJourney = async (importerId: string) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${importerId}/start_journey`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};

/**
 * 200 Response Data Object:
 * {
 *   uid: string;
 *   onboarding_stage: string;
 *   business_details: object;
 *   personal_details: object;
 *   bank_details: object;
 *   financial_details: object;
 * }
 */
export const getScfApplicationDetails = async (
  businessId: string,
  customerId: string
) => {
  return await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/application/details`,
    method: 'GET',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
};
